import { useEffect, useMemo, useState } from "react";

import { ArrowLeft, ClipboardCheck, Pencil, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { EmptyState } from "~/components/ui/empty-state";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { PageHeaderSkeleton, TableSkeleton } from "~/components/ui/skeletons";
import { TablePagination } from "~/components/ui/table-pagination";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { useReceptionModificationRequests } from "~/hooks/purchasing";
import { useOptimizedProfiles } from "~/hooks/useOptimizedProfiles";
import { usePagination } from "~/hooks/usePagination";
import { useProfileStore } from "~/store/profileStore";
import type { ReceptionModificationRequest } from "~/types/receptionModification";
import { formatDate } from "~/utils/helpers";

import { ReceptionModificationDetailSheet } from "./components/ReceptionModificationDetailSheet";
import { ReceptionModificationStatusBadge } from "./components/ReceptionModificationStatusBadge";
import { RejectReceptionModificationDialog } from "./components/RejectReceptionModificationDialog";

type StatusTab = "pending" | "approved" | "rejected";

const TAB_LABELS: Record<StatusTab, string> = {
  pending: "Pendientes",
  approved: "Aprobadas",
  rejected: "Rechazadas",
};

const PAGE_SIZE = 10;

const num = (v: number | null | undefined) =>
  v === null || v === undefined
    ? "—"
    : v.toLocaleString("es-CL", { maximumFractionDigits: 2 });

const ReceptionModificationsPage = () => {
  const navigate = useNavigate();
  const { id: currentUserId, role } = useProfileStore();
  const isApprover = role === "superadmin" || role === "admin";

  const { requests, isLoading, approve, reject, isApproving, isRejecting } =
    useReceptionModificationRequests();

  const [tab, setTab] = useState<StatusTab>("pending");
  const [selected, setSelected] = useState<ReceptionModificationRequest | null>(
    null
  );
  const [sheetOpen, setSheetOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  const profileIds = useMemo(() => {
    const ids = new Set<string>();
    for (const r of requests) {
      if (r.requested_by) ids.add(r.requested_by);
      if (r.reviewed_by) ids.add(r.reviewed_by);
    }
    return Array.from(ids);
  }, [requests]);
  const { profiles } = useOptimizedProfiles(profileIds);

  const counts = useMemo(
    () => ({
      pending: requests.filter((r) => r.status === "pending").length,
      approved: requests.filter((r) => r.status === "approved").length,
      rejected: requests.filter((r) => r.status === "rejected").length,
    }),
    [requests]
  );

  const visibleRequests = useMemo(
    () => requests.filter((r) => r.status === tab),
    [requests, tab]
  );

  const { page, setPage, pageCount, pageItems, total } = usePagination(
    visibleRequests,
    PAGE_SIZE
  );

  useEffect(() => {
    setPage(1);
  }, [tab, setPage]);

  const openDetail = (request: ReceptionModificationRequest) => {
    setSelected(request);
    setSheetOpen(true);
  };

  const handleApprove = async () => {
    if (!selected) return;
    await approve({ requestId: selected.id });
    setSheetOpen(false);
  };

  const handleReject = async (reason: string) => {
    if (!selected) return;
    await reject({ requestId: selected.id, notes: reason });
    setRejectOpen(false);
    setSheetOpen(false);
  };

  if (isLoading) {
    return (
      <PageContainer>
        <PageHeaderSkeleton />
        <TableSkeleton rows={8} columns={7} />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Solicitudes de Modificaciones"
        description="Revisa y autoriza las modificaciones de recepciones (editar o eliminar líneas)."
        back={
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 h-9 text-muted-foreground"
            onClick={() => navigate("/purchasing/orders")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver a Órdenes de Compra
          </Button>
        }
      />

      <Tabs value={tab} onValueChange={(v) => setTab(v as StatusTab)}>
        <div className="sm:hidden">
          <Select value={tab} onValueChange={(v) => setTab(v as StatusTab)}>
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(TAB_LABELS) as StatusTab[]).map((key) => (
                <SelectItem key={key} value={key}>
                  {TAB_LABELS[key]} ({counts[key]})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <TabsList className="hidden sm:inline-flex">
          {(Object.keys(TAB_LABELS) as StatusTab[]).map((key) => (
            <TabsTrigger key={key} value={key}>
              {TAB_LABELS[key]} ({counts[key]})
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent
          value={tab}
          className="mt-4 animate-in fade-in-0 slide-in-from-bottom-1 duration-300"
        >
          {visibleRequests.length === 0 ? (
            <EmptyState
              icon={ClipboardCheck}
              title={`No hay solicitudes ${TAB_LABELS[tab].toLowerCase()}`}
              description={
                tab === "pending"
                  ? "Cuando se solicite editar o eliminar una línea de recepción, aparecerá aquí para su autorización."
                  : undefined
              }
            />
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b text-gray-600">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium">Ítem</th>
                        <th className="px-4 py-3 text-left font-medium">OC</th>
                        <th className="px-4 py-3 text-left font-medium">Tipo</th>
                        <th className="px-4 py-3 text-left font-medium">
                          Solicitado por
                        </th>
                        <th className="px-4 py-3 text-left font-medium">
                          Fecha
                        </th>
                        <th className="px-4 py-3 text-right font-medium">
                          Stock actual
                        </th>
                        <th className="px-4 py-3 text-left font-medium">
                          Estado
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageItems.map((r) => (
                        <tr
                          key={r.id}
                          onClick={() => openDetail(r)}
                          className="cursor-pointer border-b last:border-0 hover:bg-muted/30"
                        >
                          <td className="px-4 py-3 font-medium text-gray-800">
                            {r.item_name || "Ítem"}
                          </td>
                          <td className="px-4 py-3 text-gray-700">
                            {r.po_number
                              ? `#${String(r.po_number).padStart(5, "0")}`
                              : "—"}
                          </td>
                          <td className="px-4 py-3">
                            <Badge
                              variant="outline"
                              className={
                                r.request_type === "delete"
                                  ? "border-red-200 bg-red-50 text-red-700"
                                  : "border-blue-200 bg-blue-50 text-blue-700"
                              }
                            >
                              {r.request_type === "delete" ? (
                                <Trash2 className="mr-1 h-3 w-3" />
                              ) : (
                                <Pencil className="mr-1 h-3 w-3" />
                              )}
                              {r.request_type === "delete"
                                ? "Eliminación"
                                : "Edición"}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-gray-700">
                            {(r.requested_by &&
                              profiles[r.requested_by]?.full_name) ||
                              "—"}
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">
                            {formatDate(r.requested_at)}
                          </td>
                          <td className="px-4 py-3 text-right font-mono text-gray-700">
                            {num(r.stock_total)}
                            {r.unit ? (
                              <span className="ml-1 text-xs text-muted-foreground">
                                {r.unit}
                              </span>
                            ) : null}
                          </td>
                          <td className="px-4 py-3">
                            <ReceptionModificationStatusBadge
                              status={r.status}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {pageCount > 1 && (
            <TablePagination
              className="mt-4"
              page={page}
              pageCount={pageCount}
              totalItems={total}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
              itemLabel="solicitudes"
            />
          )}
        </TabsContent>
      </Tabs>

      <ReceptionModificationDetailSheet
        request={selected}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        onApproveClick={handleApprove}
        onRejectClick={() => setRejectOpen(true)}
        isApprover={isApprover}
        isApproving={isApproving}
        currentUserId={currentUserId}
        profiles={profiles}
      />

      <RejectReceptionModificationDialog
        request={selected}
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        onConfirm={handleReject}
        isSubmitting={isRejecting}
      />
    </PageContainer>
  );
};

export default ReceptionModificationsPage;
